// Slice 7: hard-delete confirm modal for a non-default plant. Backend
// already enforces the default-plant guard + FK refusal, so the modal
// is here for user-side friction (this is irreversible) and for
// surfacing the FK-blocked error message inline without dismissing.
//
// Pattern matches ManufacturingPlantDealerLocationsReassignConfirm
// (scrim + centered card + ghost cancel + warn confirm) for visual
// consistency across the manufacturing suite.
import type { MfgPlant } from "@/lib/manufacturingClient";
import { ghostBtnClass, mfgConfirmDialogClass, mfgSolidDangerBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

export function ManufacturingPlantDeleteConfirm({
  open,
  plant,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  plant: MfgPlant | null;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open || !plant) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mfg-plant-delete-title"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className={`max-w-md ${mfgConfirmDialogClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id="mfg-plant-delete-title"
          className="text-base font-semibold text-gray-900 dark:text-zinc-100"
        >
          Delete plant {plant.plant_code} · {plant.name}?
        </h3>
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          This is a hard delete — the plant row is removed. If anything still
          references it (materials, units, recipes, dealer settings, or
          subscriber-location overlays), the backend will refuse and ask you to
          reassign those first. If you want to keep the row but hide it,
          use <em>Deactivate</em> instead.
        </p>
        {error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
            {error}
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            className={ghostBtnClass}
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className={mfgSolidDangerBtnClass}
            onClick={onConfirm}
            disabled={busy}
            autoFocus
          >
            {busy ? "Deleting…" : "Delete plant"}
          </button>
        </div>
      </div>
    </div>
  );
}
